/**
 * readBlogSsrSeed — map the blog hydration seed to a blog page component's
 * `initial*` props (BLOG-STD-001 full-shell SSR).
 *
 * The per-app prerender harness (`renderBlogHtml` → `buildHtmlDocument`) seeds
 * the served document with a `<script type="application/json"
 * id="__blog-ssr-seed__">{ template, locale, routePath, seed, copyrightYear }
 * </script>` **data block** (NOT an executable inline script — so it works under
 * a strict `Content-Security-Policy: script-src 'self'` with no unsafe-inline /
 * nonce / hash). Each page component's READY branch is rendered server-side via
 * its `initial*` props; for the client to hydrate that exact markup (zero
 * mismatch), `BlogTemplateRoute` must reproduce the SAME `initial*` props on
 * its first render. This helper does the mapping, mirroring
 * `renderBlogHtml.pageElementFor` 1:1.
 *
 * It is a PURE read (no mutation — safe to call during render, incl. under
 * StrictMode's double-invoke). The seed is route-matched against the current
 * params so a stale seed (after a client `<Link>` navigation to a different
 * post/category/tag) is never wrongly applied — a mismatch returns null and the
 * component falls through to its normal client fetch. `clearBlogSsrSeed()`
 * removes the data block afterward and must be called from a post-commit
 * effect, not render.
 *
 * @module @puffin-core/marketing-shell/blog/readBlogSsrSeed
 */

export const SSR_SEED_ELEMENT_ID = '__blog-ssr-seed__';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Read + JSON-parse the SSR seed data block from the DOM, if present.
 * Exported so siblings (e.g. `buildShellProps`) can share the single
 * canonical reader rather than duplicating the DOM lookup + parse.
 *
 * @returns {Object|null}
 */
export function readSsrSeedFromDom() {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(SSR_SEED_ELEMENT_ID);
  if (!el) return null;
  const text = el.textContent || '';
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    // A seed block that is not JSON (truncated in transit, or written by a broken harness) is no
    // seed: the page falls through to its normal client fetch and shows the real post. Anything
    // else is a defect (SWALLOWED-CATCH-BURNDOWN-001 U3).
    if (!(error instanceof SyntaxError)) throw error;
    return null;
  }
}

/**
 * @param {Object} args
 * @param {'blog-index'|'blog-post'|'blog-category'|'blog-tag'} args.template
 * @param {Object} [args.params]  react-router useParams() (slug / category / tag)
 * @returns {Object|null} `initial*` props for the matching page component, or null
 *   when there is no seed / a different template / a param mismatch.
 */
export function readBlogSsrSeed({ template, params } = {}) {
  const ssr = readSsrSeedFromDom();
  if (!ssr || typeof ssr !== 'object' || ssr.template !== template) return null;
  const seed = ssr.seed && typeof ssr.seed === 'object' ? ssr.seed : {};

  if (template === 'blog-index') {
    return {
      initialPosts: asArray(seed.posts),
      initialOffset: typeof seed.offset === 'number' ? seed.offset : 0,
      // WS-J — the SERVER's page size wins when it seeds one. marketing-shell
      // cannot import marketing-cms's BLOG_INDEX_PAGE_SIZE (no dependency), so
      // taking the seeded value is what guarantees the SSR HTML and the hydrated
      // view agree on which posts page N holds. Omitted (undefined) when absent,
      // so the component's own default applies and unseeded callers are
      // byte-identical.
      ...(typeof seed.pageSize === 'number' && seed.pageSize > 0
        ? { pageSize: seed.pageSize }
        : {}),
      // SEO-CRAWL-001 — the real published total. Only the server can know it
      // (the client sees one window at a time), and without it the control could
      // only render "Page N" and could never say which page was last. Omitted
      // when absent so an unseeded render is unchanged.
      ...(typeof seed.totalPublishedCount === 'number'
        ? { totalKnownCount: seed.totalPublishedCount }
        : {}),
    };
  }
  if (template === 'blog-post') {
    const resolved = seed.post;
    if (!resolved || typeof resolved !== 'object') return null;
    const slug = resolved.meta?.slug || resolved.post?.slug || resolved.slug;
    if (params && params.slug && slug && params.slug !== slug) return null;
    return { initialData: resolved, initialRelated: asArray(seed.related) };
  }
  if (template === 'blog-category') {
    const category = seed.category;
    if (!category || typeof category !== 'object') return null;
    if (params && params.category && category.slug && params.category !== category.slug)
      return null;
    return { initialCategory: category, initialPosts: asArray(seed.posts) };
  }
  if (template === 'blog-tag') {
    const tag = seed.tag;
    if (!tag || typeof tag !== 'object') return null;
    if (params && params.tag && tag.slug && params.tag !== tag.slug) return null;
    return { initialTag: tag, initialPosts: asArray(seed.posts) };
  }
  return null;
}

/**
 * Free the one-shot SSR seed after it has been consumed by the hydration render.
 * Call from a post-commit `useEffect` (NOT during render) so a later client
 * navigation cannot re-read a stale seed.
 *
 * Removes the `<script type="application/json" id="__blog-ssr-seed__">` element
 * from the DOM. `readBlogSsrSeed` / `readSsrSeedFromDom` treat an absent element
 * as no seed, so subsequent calls return null.
 *
 * @returns {void}
 */
export function clearBlogSsrSeed() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(SSR_SEED_ELEMENT_ID);
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
}

export default readBlogSsrSeed;
